<template>
  <!--
    P4 AI Tutor -- setup (SetupCard) -> real `createSession` -> chat shell
    (WP-03: empty/welcome state, plain-text turns, first real `createTurn` +
    SSE streaming lifecycle).

    WP-02C-JOURNEY-PARITY (human-closed decision, doc 11, 2026-09-17):
    the pre-chat setup is ONE consolidated screen (SetupCard), matching the
    canonical prototype's single "flow-card" -- not a wizard of separate
    Intent/Context screens. `?intent=` entry (P3, StudentHomeRich's
    quick-intent cards) still works exactly as before: a known intent in
    the query string preselects that intent pill in the SAME screen,
    rather than skipping a separate screen.

    Also reuses the same accepted shell DashboardView uses: `student-app-header`
    suppresses the legacy global masthead for this route too (see
    `src/components/App/index.vue`'s `showHeader`), so this route no longer
    falls back to the pre-P3 header when navigated to from Dashboard.
  -->
  <div class="student-ai-tutor-view" data-testid="student-ai-tutor-view">
    <!-- STUDENT-UX-02 / UX2-01 (doc 30 §6, D-1): a Learn conversation is
         titled by its topic; everything else keeps the fixed title. -->
    <student-app-header
      :title="headerTitle"
      :ai-tutor-credits-remaining="aiTutorCreditsRemaining"
    />

    <!--
      WP-03-CHAT-VISUAL-PARITY (doc 11): the chat state is composed exactly as
      the rendered canonical prototype draws it -- a ~920px column on the page
      canvas (no enclosing card): progress bar, context bar, timeline, and a
      composer pinned to the bottom. Functional wiring (SSE open before the
      composer is usable, submit -> sendMessage) is unchanged.
    -->
    <!--
      V405-B (doc 26 §6): before the first Student turn the chat shows the V405
      start state -- a stage holding the start copy, the ways to start that
      really exist today, and the SAME composer docked inside it. `chatStart`
      is exactly the empty-state predicate this view already had; the first
      Send sets `pendingStudentTurn`, so everything below is the accepted
      active chat again from that moment, unchanged.
    -->
    <div
      v-if="activeSession"
      class="ai-tutor-chat"
      :class="{
        'ai-tutor-chat--start': chatStart,
        'ai-tutor-chat--credit-notice': creditsExhausted && !chatStart,
        'ai-tutor-chat--action-notice': actionRecoveryPending && !chatStart,
      }"
      data-testid="ai-tutor-chat"
    >
      <chat-progress :stage="progressStage" />
      <chat-context-bar
        :intent="(activeSetupContext && activeSetupContext.intent) || ''"
        :topic="(activeSetupContext && activeSetupContext.topic) || ''"
        :course-label="contextCourseLabel"
        :context-phrase="contextPhraseLabel"
      />

      <!-- V405-F1 (doc 26 §12, F1-3, human decision 2026-09-29): the
           conversation strip is gone from the chat surface. Conversation
           history lives in Reports; nothing is deleted. -->

      <div class="ai-tutor-chat__timeline">
        <section
          v-if="chatStart"
          class="ai-tutor-chat-start"
          data-testid="ai-tutor-empty-chat"
          :aria-label="$t('aiTutorSetup.chatStart.label')"
        >
          <div class="ai-tutor-chat-start__copy">
            <div class="ai-tutor-chat-start__eyebrow">
              <i aria-hidden="true"></i>
              <span>{{ $t('aiTutorSetup.chatStart.eyebrow') }}</span>
            </div>
            <h1 class="ai-tutor-chat-start__title">
              <span class="ai-tutor-chat-start__friend"
                >{{ $t('aiTutorSetup.chatStart.friend') }}
                <span aria-hidden="true">👋</span></span
              >
              <span class="ai-tutor-chat-start__main">{{
                $t('aiTutorSetup.chatStart.title')
              }}</span>
            </h1>
            <p class="ai-tutor-chat-start__support">
              {{ $t('aiTutorSetup.chatStart.support') }}
            </p>
          </div>

          <!-- Only the ways to start that exist for this Student: typing and a
               photo -- voice, worded as V405 words it, only where Subway
               offers it (37_VOICE_TO_TEXT) -- and the Whiteboard, V405's third
               way, only where the composer's Board trigger is offered
               (Package 38 UX-3). Guidance only -- nothing here is a control. -->
          <div
            class="ai-tutor-chat-start__guide"
            :class="{
              'ai-tutor-chat-start__guide--three': startWays.length === 3,
            }"
            data-testid="ai-tutor-start-guide"
          >
            <div
              v-for="way in startWays"
              :key="way.key"
              class="ai-tutor-chat-start__way"
              :data-testid="'ai-tutor-start-way-' + way.key"
            >
              <span class="ai-tutor-chat-start__way-icon" aria-hidden="true">
                <student-home-icon :name="way.icon" :size="18" />
              </span>
              <span class="ai-tutor-chat-start__way-copy">
                <strong>{{
                  $t(`aiTutorSetup.chatStart.guide.${way.key}.title`)
                }}</strong>
                <span>{{
                  $t(`aiTutorSetup.chatStart.guide.${way.key}.sub`)
                }}</span>
              </span>
            </div>
          </div>
        </section>

        <!-- Keyed by session: opening another conversation is a fresh list
             (its own scroll-follow state, landing on its latest message). -->
        <turn-list
          v-else
          :key="activeSession.id"
          :turns="turns"
          :processing-state="processingState"
          :student-initial-source="studentInitialSource"
          :pending-turn="visiblePendingTurn"
          :awaiting-reply="awaitingTutorReply"
          :feedback-by-turn-id="feedbackByTurnId"
          :feedback-enabled="feedbackEnabled"
          :resend-disabled="composerDisabled"
          :media-source-by-turn-id="mediaSourceByTurnId"
          :board-share-by-turn-id="boardShareByTurnId"
          :actions-disabled="composerDisabled"
          :stream-status="streamStatus"
          @pick-suggestion="onPickSuggestion"
          @resend="onResend"
          @feedback="onFeedback"
          @dismiss-feedback="onDismissFeedback"
          @media-expired="onMediaExpired"
          @end-conversation="onEndConversation"
          @new-conversation="onNewQuestion"
        />
      </div>

      <!-- D4 (doc 11): a compact, non-blocking note near the composer for a
           failed send / open / new conversation. The raw server message is
           never shown. FINAL CHAT V2: the note names what went wrong where
           Subway's stable code says so. -->
      <p
        v-if="error && !creditsErrorShownAsWarning && !actionRecoveryPending"
        class="ai-tutor-chat__error"
        role="alert"
        data-testid="ai-tutor-chat-error"
      >
        {{ errorNote }}
      </p>
      <!-- P40 QA-28: the Board's code could not be fetched (offline): say
           so instead of the trigger silently doing nothing. -->
      <p
        v-if="boardOpenFailed"
        class="ai-tutor-chat__error"
        role="alert"
        data-testid="ai-tutor-board-open-error"
      >
        {{ $t('aiTutorSetup.chat.errors.network') }}
      </p>

      <composer
        ref="composer"
        :disabled="composerDisabled"
        :docked="chatStart"
        :first-question="turns.length === 0 && !visiblePendingTurn"
        :guided="clarifying"
        :board-entry="boardEntryShown"
        :board-entry-disabled="boardEntryDisabled"
        :voice="composerVoice"
        @submit="onComposerSubmit"
        @open-board="openBoard"
        @voice-start="onVoiceStart"
        @voice-stop="onVoiceStop"
        @voice-cancel="onVoiceCancel"
        @voice-dismiss="onVoiceDismiss"
      >
        <!-- CR-03 (doc 28 §17/§18): the prototype's zero-credit warning,
             directly above the composer, while Subway's known balance is 0.
             AT-API-020-H1 (doc 34/01 §3.3): the refused-action notice shares
             that area -- the failure is explained there, with the Student's
             two choices, instead of by the generic note above. -->
        <template v-if="creditsExhausted || actionRecoveryPending" #notice>
          <chat-credit-warning v-if="creditsExhausted" />
          <chat-action-recovery
            v-if="actionRecoveryPending"
            :disabled="composerDisabled"
            @send-plain="onSendWithoutAction"
            @dismiss="onDismissActionRecovery"
          />
        </template>
      </composer>

      <!-- POST-UAT-UX / PUX-B (doc 35/01 §2): the conversation's Whiteboard
           as the rendered V405 sheet over this chat -- the route, the
           session, its stream and the composer's draft all stay. Loaded
           only when opened (the Board editor is the Workspace's own lazy
           chunk). -->
      <chat-board-sheet
        v-if="boardSheetOpen && boardEntryShown"
        :course-name="activeSetupContext.subject"
        :session-id="activeSession.id"
        :blocked-by="boardBlockedBy"
        @close="closeBoard"
      />
    </div>

    <setup-card
      v-else
      :starting="startingSession"
      :submit-error="error"
      :initial-intent="initialIntent"
      :initial-grade="restoredSetupContext.grade"
      :initial-subject="restoredSetupContext.subject"
      :initial-topic="restoredSetupContext.topic"
      @context-labels="onSetupContextLabels"
      @submit="onSetupSubmit"
    />
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import SetupCard from '@/components/AiTutor/Setup/SetupCard.vue'
import StudentAppHeader from '@/components/StudentShell/StudentAppHeader.vue'
import TurnList from '@/components/AiTutor/Chat/TurnList.vue'
import Composer from '@/components/AiTutor/Chat/Composer.vue'
import ChatProgress from '@/components/AiTutor/Chat/ChatProgress.vue'
import ChatContextBar from '@/components/AiTutor/Chat/ChatContextBar.vue'
import ChatCreditWarning from '@/components/AiTutor/Chat/ChatCreditWarning.vue'
import ChatActionRecovery from '@/components/AiTutor/Chat/ChatActionRecovery.vue'
import StudentHomeIcon from '@/components/StudentHome/StudentHomeIcon.vue'
import { collaborators as aiTutorCollaborators } from '@/store/modules/aiTutor'
import composerVoice, {
  voiceCapabilityFrom,
} from '@/components/AiTutor/Chat/composerVoice'

const KNOWN_INTENTS = ['troubleshoot', 'review', 'learn']

// PUX-B: the Board sheet's chunk. `openBoard` loads it BEFORE the sheet is
// shown, so a failed load (network, a chunk renamed by a redeploy) leaves the
// trigger usable for another try instead of a sheet that never renders.
const loadChatBoardSheet = () =>
  import(
    /* webpackChunkName: "ai-tutor-board" */ '@/components/AiTutor/Board/ChatBoardSheet.vue'
  )

// V405-B: the ways to start the chat start state names -- one per capability
// the composer really has on this branch (text, photo).
const START_WAYS = [
  { key: 'type', icon: 'compose' },
  { key: 'image', icon: 'image' },
]
// Package 38 UX-3: V405's third way, the Whiteboard -- named only where the
// Board trigger itself is offered (`boardEntryShown`).
const WHITEBOARD_WAY = { key: 'whiteboard', icon: 'board' }

// FINAL CHAT V2 -- Subway error code -> `aiTutorSetup.chat.errors.*`.
const ERROR_NOTES = {
  AI_TUTOR_BAD_ATTACHMENT: 'badAttachment',
  AI_TUTOR_SESSION_CLOSED: 'sessionClosed',
  AI_TUTOR_SESSION_NOT_FOUND: 'sessionClosed',
  AI_TUTOR_SUGGESTION_REJECTED: 'staleChoice',
  AI_TUTOR_ASK_REJECTED: 'staleChoice',
  AI_TUTOR_COURSE_NOT_PROVISIONED: 'notProvisioned',
  // SX-03B (doc 22 §6.3): the real fail-closed code for a course without a
  // Synapse mapping, which every chat route can also answer with.
  SYNAPSE_COURSE_MAPPING_MISSING: 'notProvisioned',
  AI_TUTOR_COURSE_HAS_NO_SOURCES: 'notProvisioned',
  AI_TUTOR_MODE_DISABLED: 'rejected',
  AI_TUTOR_REQUEST_REJECTED: 'rejected',
  // AT-API-020-H1: a refused action is explained by its own notice; this
  // row only names the failure if that notice is not on screen.
  AI_TUTOR_ACTION_REJECTED: 'rejected',
  // CR-03 (doc 28 §18): the Student's other question is still running.
  AI_TUTOR_TURN_IN_PROGRESS: 'turnInProgress',
  // P40 QA-22: a turn text over 4,000 characters (422) or a request body
  // over Subway's parser limit (413).
  AI_TUTOR_TEXT_TOO_LONG: 'textTooLong',
  REQUEST_BODY_TOO_LARGE: 'textTooLong',
}

export default {
  name: 'StudentAiTutorView',
  components: {
    SetupCard,
    StudentAppHeader,
    TurnList,
    Composer,
    ChatProgress,
    ChatContextBar,
    ChatCreditWarning,
    ChatActionRecovery,
    StudentHomeIcon,
    // PUX-B: the Board editor is large -- fetched only when the sheet opens,
    // in the Board Workspace's own chunk.
    ChatBoardSheet: loadChatBoardSheet,
  },
  // 37_VOICE_TO_TEXT: `voiceAvailable`, `composerVoice`, `voiceBusy` and the
  // composer's voice events.
  mixins: [composerVoice],
  data() {
    return {
      // Preserves the existing `?intent=` acknowledgment behavior (P3): a
      // known intent in the query string preselects that pill in SetupCard.
      initialIntent: KNOWN_INTENTS.includes(this.$route.query.intent)
        ? this.$route.query.intent
        : '',
      // WP-03 binding lifecycle step 2: the SSE subscription must be open
      // BEFORE the composer is usable -- true only once `openTutorStream`
      // has been dispatched for the current activeSession.
      chatReady: false,
      // WP-04V-E (D6): the «ریاضی دهم» label SetupCard hands over for the
      // chat's context summary. Presentation state only -- it is never sent
      // anywhere, so a page reload simply drops back to the intent-only
      // summary rather than showing a stale or invented course.
      contextCourseLabel: '',
      // The per-intent trailing choice, e.g. «در یکی از مراحل گیر کردم».
      // Local-only UI state in SetupCard; presentation-only here too.
      contextPhraseLabel: '',
      // POST-UAT-UX / PUX-B: Subway's server-driven whiteboard capability
      // (the Reports summary, the source the Reports Board entry uses);
      // unknown or unreadable = no Board entry.
      whiteboardAvailable: false,
      // The Board sheet is open over this chat / its chunk is being loaded.
      boardSheetOpen: false,
      boardOpening: false,
      // P40 QA-28: the last attempt to open the Board could not load it.
      boardOpenFailed: false,
    }
  },
  computed: {
    ...mapState('aiTutor', [
      'activeSession',
      'activeSetupContext',
      'startingSession',
      'processingState',
      'error',
      'sendingTurn',
      'pendingStudentTurn',
      'feedbackByTurnId',
      'mediaSourceByTurnId',
      // WB-M2A: the «از تخته» records of a board picture in this conversation.
      'boardShareByTurnId',
      // Doc 27 (ST-02): shown only as "reconnecting", while a reply is awaited.
      'streamStatus',
      // UX2-01: the Learn setup's one-shot first turn, if any.
      'pendingInitialTurn',
    ]),
    ...mapGetters('aiTutor', [
      'awaitingTutorReply',
      'feedbackInFlight',
      // AT-API-020-H1: the Tutor refused the action of the message that is
      // on screen as failed; the Student is asked what to do.
      'actionRecoveryPending',
    ]),
    // UX2-01 (D-1): the topic of an OPEN Learn conversation is the page title.
    // Outside a conversation (the setup) `activeSetupContext` may still hold
    // the previous conversation's topic for Reports, so it is never shown then.
    learnTopic() {
      const context = this.activeSetupContext
      return this.activeSession &&
        context &&
        context.intent === 'learn' &&
        typeof context.topic === 'string' &&
        context.topic.trim()
        ? context.topic.trim()
        : ''
    },
    headerTitle() {
      return this.learnTopic || this.$t('aiTutorSetup.headerTitle')
    },
    // UX2-01: the one-shot first turn is due exactly when the chat is ready
    // for its session, still in the start state, the store can take a message
    // right now (nothing of a previous conversation still sending or awaiting
    // feedback -- `sendMessage` would otherwise refuse it silently), and the
    // pending turn is for THIS session. Until then it simply stays pending.
    initialTurnDue() {
      const pending = this.pendingInitialTurn
      return Boolean(
        this.chatReady &&
          this.chatStart &&
          !this.sendingTurn &&
          !this.feedbackInFlight &&
          pending &&
          this.activeSession &&
          pending.sessionId === this.activeSession.id
      )
    },
    // CR-03 (doc 28 §16): Subway's balance as the user store holds it.
    aiTutorCreditsRemaining() {
      const credits = this.$store.getters['user/aiTutorCreditsRemaining']
      return typeof credits === 'number' ? credits : null
    },
    // CR-03 (doc 28 §14): only a KNOWN zero gates new sends. null (unknown,
    // or not reported yet) is never treated as zero -- Subway enforces anyway.
    creditsExhausted() {
      const credits = this.aiTutorCreditsRemaining
      return typeof credits === 'number' && credits <= 0
    },
    // POST-UAT-UX / PUX-B (doc 35/00 §3.2) -- the V405 Board trigger in the
    // composer: only where the Board exists for this Student, for an open
    // conversation (also its empty start).
    boardEntryShown() {
      return (
        this.whiteboardAvailable &&
        !!this.activeSession &&
        !!this.activeSession.id &&
        !!(this.activeSetupContext && this.activeSetupContext.subject)
      )
    },
    // V405 dims it while the chat is busy; the guided «نفهمیدم» step closes
    // the photo action and closes this image path too. Never the balance:
    // the Board costs nothing (its share sheet has the credit gate).
    boardEntryDisabled() {
      return (
        !this.chatReady ||
        this.sendingTurn ||
        this.feedbackInFlight ||
        this.awaitingTutorReply ||
        this.clarifying ||
        this.boardSheetOpen ||
        this.boardOpening ||
        // 37_VOICE_TO_TEXT: V405 closes the Board while it listens or
        // transcribes.
        this.voiceBusy
      )
    },
    // The share sheet's gate -- the Board Workspace's own reasons.
    boardBlockedBy() {
      if (!this.chatReady || !this.activeSession) return 'chat'
      if (this.creditsExhausted) return 'credits'
      if (this.sendingTurn || this.feedbackInFlight) return 'busy'
      return null
    },
    // The 402 is explained by the warning itself, not by a second note.
    creditsErrorShownAsWarning() {
      return !!this.error && this.error.kind === 'credits_exhausted'
    },
    // STUDENT CHAT V2 -- the optimistic message belongs to one session only.
    visiblePendingTurn() {
      const pending = this.pendingStudentTurn
      return pending &&
        this.activeSession &&
        pending.sessionId === this.activeSession.id
        ? pending
        : null
    },
    // CR-03: at a known zero balance every new send is gated -- the composer,
    // a failed message's resend, suggestions and ask answers (all fed from
    // here). Feedback and «پایان گفت‌وگو» are not sends and stay available.
    composerDisabled() {
      return (
        !this.chatReady ||
        this.sendingTurn ||
        this.feedbackInFlight ||
        this.creditsExhausted
      )
    },
    // FINAL CHAT V2 -- the chat's error note, by Subway's stable code (the
    // upstream business errors, AiTutorRequestRejectedError) or failure kind.
    errorNote() {
      const error = this.error || {}
      const key =
        ERROR_NOTES[error.code] || (error.kind === 'network' ? 'network' : '')
      return key
        ? this.$t(`aiTutorSetup.chat.errors.${key}`)
        : this.$t('aiTutorSetup.chat.actionErrorNote')
    },
    // «فهمیدم / نفهمیدم» is offered on an open session with nothing else in
    // flight, and not while the tutor is waiting on its own question
    // (`pendingAction`) -- the student answers that first.
    feedbackEnabled() {
      const session = this.activeSession
      return (
        this.chatReady &&
        !this.sendingTurn &&
        !!session &&
        session.status !== 'closed' &&
        !session.pendingAction
      )
    },
    // V2's guided composer: «نفهمیدم» was recorded without a re-teach, so the
    // student is asked to write what is unclear.
    clarifying() {
      if (this.visiblePendingTurn) return false
      const last = this.turns[this.turns.length - 1]
      if (!last || last.role === 'student') return false
      const entry = (this.feedbackByTurnId || {})[last.id]
      return (
        !!entry &&
        entry.kind === 'confused' &&
        entry.status === 'recorded' &&
        !entry.reteachTurnId
      )
    },
    // A failed `createSession` leaves the student's selection in
    // `activeSetupContext` and an `error`; a retry restores exactly that
    // selection. Without an error the setup is a NEW conversation (Student
    // Journey V2): it opens on the reference's defaults, never on the previous
    // conversation's course -- `activeSetupContext` is kept only so Reports
    // still knows which course's history to list.
    restoredSetupContext() {
      const context = this.error ? this.activeSetupContext : null
      return {
        grade: (context && context.grade) || '',
        subject: (context && context.subject) || '',
        topic: (context && context.topic) || '',
      }
    },
    turns() {
      const sessionId = this.activeSession && this.activeSession.id
      if (!sessionId) return []
      return this.$store.state.aiTutor.turnsBySessionId[sessionId] || []
    },
    // V405-B: THE boundary between the start state and the active chat --
    // the empty-state predicate this view has always used, unchanged.
    chatStart() {
      return (
        !!this.activeSession &&
        this.turns.length === 0 &&
        this.processingState.phase === 'idle' &&
        !this.pendingStudentTurn
      )
    },
    // 37_VOICE_TO_TEXT: where Subway offers voice, V405's first way names it
    // («تایپ کن یا حتی ویس بده»); otherwise the guide is unchanged.
    // Package 38 UX-3: where the Board is offered, V405's third way follows.
    startWays() {
      const ways = this.voiceAvailable
        ? [{ key: 'typeOrVoice', icon: 'compose' }, ...START_WAYS.slice(1)]
        : START_WAYS
      return this.boardEntryShown ? [...ways, WHITEBOARD_WAY] : ways
    },
    // Presentation only: the progress bar moves to its last step once the
    // conversation has started (a turn exists, or a reply is on its way).
    progressStage() {
      const started =
        this.turns.length > 0 ||
        !!this.visiblePendingTurn ||
        ['processing', 'streaming'].includes(this.processingState.phase)
      return started ? 'answer' : 'ask'
    },
    // The student avatar shows the user's own first-name initial; empty when
    // no name is known (TurnBubble then falls back to the role label).
    studentInitialSource() {
      const userState = this.$store.state.user
      return (userState && userState.user && userState.user.firstname) || ''
    },
  },
  watch: {
    // WP-03 binding lifecycle step 13: stop the previous stream and open a
    // new one whenever the active session id changes (covers both
    // setup -> first session, and any future session switch).
    'activeSession.id': {
      immediate: true,
      handler(sessionId, previousSessionId) {
        if (sessionId === previousSessionId) return
        // PUX-B: the sheet belongs to the conversation it was opened on.
        this.boardSheetOpen = false
        // 37_VOICE_TO_TEXT: and so does a recording or transcript under way.
        this.resetVoice()
        this.chatReady = false
        this.$store.dispatch('aiTutor/stopStreaming')
        if (!sessionId) return
        this.$store.dispatch('aiTutor/openTutorStream')
        this.chatReady = true
      },
    },
    // UX2-01 (D-1 / D-2): consume the one-shot first turn exactly once.
    initialTurnDue: {
      immediate: true,
      handler(due) {
        if (due) this.consumePendingInitialTurn()
      },
    },
  },
  mounted() {
    this.loadCapabilities()
  },
  beforeDestroy() {
    this.$store.dispatch('aiTutor/stopStreaming')
  },
  methods: {
    // PUX-B: best-effort and silent -- a failure leaves the entry hidden.
    // 37_VOICE_TO_TEXT: the same single summary read also says whether the
    // composer offers voice; a failure keeps the inert microphone.
    async loadCapabilities() {
      let available = false
      let voice = false
      try {
        const api = aiTutorCollaborators.api
        const summary =
          api && typeof api.getReportsSummary === 'function'
            ? await api.getReportsSummary()
            : null
        const capabilities = summary && summary.capabilities
        available = !!capabilities && capabilities.whiteboard === 'available'
        voice = voiceCapabilityFrom(summary)
      } catch (err) {
        available = false
        voice = false
      }
      if (this._isDestroyed) return
      this.whiteboardAvailable = available
      this.voiceAvailable = voice
    },
    async openBoard() {
      if (!this.boardEntryShown || this.boardEntryDisabled) return
      this.boardOpening = true
      this.boardOpenFailed = false
      let loaded = true
      try {
        await loadChatBoardSheet()
      } catch (err) {
        loaded = false
      }
      if (this._isDestroyed) return
      this.boardOpening = false
      this.boardOpenFailed = !loaded
      if (loaded && this.boardEntryShown) this.boardSheetOpen = true
    },
    // A share was sent: the conversation is where the Student looks next.
    // Otherwise the focus goes back to the trigger.
    closeBoard({ sent } = {}) {
      this.boardSheetOpen = false
      const composer = this.$refs.composer
      if (!composer) return
      if (sent) composer.focus()
      else if (typeof composer.focusBoardEntry === 'function') {
        composer.focusBoardEntry()
      }
    },
    // WP-04V-E (D6): presentation labels only. Deliberately separate from
    // `onSetupSubmit` so the dispatched `startSession` payload is untouched.
    onSetupContextLabels({ courseLabel, contextPhrase }) {
      this.contextCourseLabel = courseLabel || ''
      this.contextPhraseLabel = contextPhrase || ''
    },
    onSetupSubmit({
      intent,
      grade,
      subject,
      topic,
      stuckReason,
      proficiency,
      studyField,
      initialQuestion,
      topicNodeId,
    }) {
      const context = { intent, grade, subject, topic }
      // 36 TC-2: a Learn topic picked from the course outline names its
      // chapter; Subway checks it and stores the provider's title.
      if (intent === 'learn' && topic && topicNodeId) {
        context.topicNodeId = topicNodeId
      }
      // The intention's sub-choice: Borna's own setup record (the creation
      // snapshot, the context bar). AT-API-020-H1: it is not sent to the
      // Tutor -- API 0.2.0 has no intake, and Borna sends no `context`.
      if (stuckReason) context.stuckReason = stuckReason
      if (proficiency) context.proficiency = proficiency
      // CAT-1A: the profile study field shown in the setup, for Borna's
      // creation-time snapshot only (never sent to the tutor).
      if (studyField) context.studyField = studyField
      // UX2-01 (D-1): the optional first message rides along to
      // `startSession`, which strips it from the setup context / snapshot and
      // keeps it as the one-shot first turn of the NEW session only.
      if (typeof initialQuestion === 'string' && initialQuestion.trim()) {
        context.initialQuestion = initialQuestion.trim()
      }
      this.$store.dispatch('aiTutor/startSession', context)
    },
    // UX2-01: the one-shot. The flag is cleared FIRST, so no second dispatch
    // can ever follow (a failed send keeps its existing retry bubble; the
    // watcher sees no pending turn any more). At a known zero balance (D-2)
    // nothing is sent or charged: the exact text goes into the composer's
    // draft and the existing credit warning stays.
    consumePendingInitialTurn() {
      const pending = this.pendingInitialTurn
      const session = this.activeSession
      if (!pending || !session || pending.sessionId !== session.id) return
      const text = pending.text
      this.$store.commit('aiTutor/setPendingInitialTurn', null)
      if (!text) return
      if (this.creditsExhausted) {
        this.prefillDraft(text)
        return
      }
      this.$store.dispatch('aiTutor/sendMessage', { text })
      // The store takes a message synchronously (its optimistic bubble is
      // committed before the first network call) or refuses it without a
      // trace. A refused first question is never lost: it becomes the draft,
      // for the student to send when the chat can.
      const taken = this.$store.state.aiTutor.pendingStudentTurn
      if (!taken || taken.sessionId !== session.id) this.prefillDraft(text)
    },
    // The watcher may run before the chat (and its composer) is rendered for
    // the session that was just created: fill the draft once it exists.
    prefillDraft(text) {
      const prefill = () => {
        const composer = this.$refs.composer
        if (composer && typeof composer.setDraft === 'function') {
          composer.setDraft(text)
          return true
        }
        return false
      }
      if (!prefill()) {
        this.$nextTick(() => {
          if (!prefill()) this.$nextTick(prefill)
        })
      }
    },
    // STUDENT CHAT V2 image: `image` ({file, fileFormat, previewUrl, source})
    // travels with the text when the student attached a photo.
    onComposerSubmit({ text, image }) {
      this.$store.dispatch(
        'aiTutor/sendMessage',
        image ? { text, image } : { text }
      )
    },
    onResend() {
      this.$store.dispatch('aiTutor/retryPendingTurn')
    },
    // AT-API-020-H1 -- a suggestion card: its `text` is sent as an ordinary
    // message (API 0.2.0 has no suggestion id).
    onPickSuggestion(suggestion) {
      this.$store.dispatch('aiTutor/sendMessage', { suggestion })
    },
    // AT-API-020-H1 (doc 34/01 §3.3) -- the two choices of the refused-action
    // notice. Each is the Student's own tap: one resend with no action, or
    // nothing at all.
    onSendWithoutAction() {
      this.$store.dispatch('aiTutor/resendWithoutAction')
    },
    onDismissActionRecovery() {
      this.$store.dispatch('aiTutor/dismissActionRecovery')
    },
    async onFeedback({ turnId, kind }) {
      await this.$store.dispatch('aiTutor/submitFeedback', { turnId, kind })
      // V2 hands the student straight to the composer when asked to say what
      // is unclear.
      if (kind === 'confused' && this.clarifying && this.$refs.composer) {
        this.$refs.composer.focus()
      }
    },
    onDismissFeedback(turnId) {
      this.$store.dispatch('aiTutor/dismissFeedback', turnId)
    },
    // A persisted photo's signed URL expired (15 min upstream): one bounded
    // refetch brings freshly signed URLs.
    onMediaExpired(turnId) {
      this.$store.dispatch('aiTutor/refreshExpiredMedia', turnId)
    },
    // V405-F1 (doc 26 §12.4). Both completion actions clear ONLY the open
    // conversation, with the store action the Home hero already uses: no API
    // call, nothing deleted -- the conversation stays in Reports and can be
    // resumed. The `activeSession.id` watcher stops its stream.
    //
    // «پایان گفت‌وگو»: back to Home.
    onEndConversation() {
      this.$store.dispatch('aiTutor/beginNewConversation')
      this.$router.push({
        name: 'DashboardView',
        params: { lang: this.$route.params.lang },
      })
    },
    // «+ سؤال یا یادگیری جدید»: this same route then shows the setup; a new
    // conversation exists only once the student submits it.
    onNewQuestion() {
      this.$store.dispatch('aiTutor/beginNewConversation')
    },
  },
}
</script>

<style lang="scss" scoped>
// WP-03-CHAT-VISUAL-PARITY (doc 11) -- every chat value below was measured
// from the rendered canonical prototype (sha256 75678bee...; 1920x1040 and
// 390x844). D1: the chat UI uses the prototype's exact px type scale; the
// project font-family (IRANSans) is inherited, not overridden.
$ai-tutor-ink: $borna-ink;
$ai-tutor-text: $borna-text;
$ai-tutor-indigo: $borna-indigo-600;
$ai-tutor-indigo-50: $borna-indigo-50;
$ai-tutor-indigo-100: $borna-indigo-100;
$ai-tutor-indigo-300: $borna-indigo-300;
$ai-tutor-muted: $borna-muted;
$ai-tutor-page-bg: $borna-canvas;

.student-ai-tutor-view {
  background: $ai-tutor-page-bg;
  min-height: 100%;
}

// ~920px column on the canvas, no card. The composer is the last flex child,
// so `margin-top: auto` + sticky keeps it at the viewport bottom even while
// the conversation is short. The min-height subtracts the 72px sticky header.
.ai-tutor-chat {
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  // 920px column + 2 x 38px prototype side padding
  max-width: $borna-chat-column + 2 * $borna-chat-gutter;
  min-height: calc(100vh - #{$borna-header-height});
  margin: 0 auto;
  // WP-04V-D (D10): the composer is `position: fixed` now, so the column
  // reserves its footprint here -- 60px tall, 20px clear of the floor -- and
  // the last turn can never scroll under it.
  padding: 34px $borna-chat-gutter 80px;
  color: $ai-tutor-text;
  font-size: 14px;
  line-height: 1.75;
}

.ai-tutor-chat__timeline {
  margin-bottom: 18px;
}

// D4 (doc 11): compact, non-blocking note above the composer.
.ai-tutor-chat__error {
  align-self: flex-start;
  max-width: 100%;
  margin: 0 0 10px;
  padding: 8px 12px;
  border: 1px solid #f3b9b3;
  border-radius: 12px;
  background: #fdecea;
  color: #b3261e;
  font-size: 11px;
}

// ---------------------------------------------------------------------------
// V405-B (doc 26 §6) -- the chat START state. Every value was measured from
// the RENDERED V405 reference (`BORNA_final405-07-06.html`, sha256
// ea21cc7b...9cfd) at 1440x900 and 390x844: `.borna-chat-stage`,
// `.borna-v12-start-shell`, `.borna-v12-start-copy`, `.borna-v12-input-guide`,
// with the reference's own 900px and 620px tiers.
//
// The stage is ONE surface behind the start copy and the docked composer. It
// is drawn by a pseudo-element spanning their two grid rows, so the timeline
// and the composer stay the siblings they are in the active chat (same
// component instance, no wrapper) and the stage grows with the composer.
// ---------------------------------------------------------------------------
$start-stage-border: #b9c8f1;
$start-stage-pad-x: 24px;
$start-stage-pad-bottom: 20px;
$start-stage-gap-top: clamp(24px, 6vh, 64px);
$start-stage-gap-bottom: 32px;
$start-tier-tablet: 900.98px;
// Package 38 UX-3: V405's 620px tier, used by the three-way start guide only.
$start-tier-guide-phone: 620.98px;

// The composer is in flow here, so the column no longer reserves the fixed
// composer's footprint -- only the page's own floor (and, in the mobile shell,
// the bottom nav).
.ai-tutor-chat.ai-tutor-chat--start {
  padding-bottom: 34px;

  @include borna-mobile {
    padding-bottom: calc(
      #{$borna-bottom-nav-clearance + 16px} + env(safe-area-inset-bottom)
    );
  }
}

.ai-tutor-chat--start {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  align-content: start;

  &::before {
    content: '';
    grid-column: 1;
    grid-row: 3 / span 2;
    margin: $start-stage-gap-top 0 $start-stage-gap-bottom;
    border: 1px solid $start-stage-border;
    border-radius: 26px;
    background: linear-gradient(145deg, #f8fbff 0%, #edf3ff 58%, #e8f0ff 100%);
    box-shadow: 0 20px 52px rgba(49, 75, 214, 0.12);
  }

  // Grid items' margins do not collapse as they do in the reference's block
  // flow: without this the context bar's own bottom margin would be added to
  // the stage's gap (measured 20px low once the conversation strip was gone).
  > .ai-tutor-context {
    margin-bottom: 0;
  }

  .ai-tutor-chat__timeline {
    grid-column: 1;
    grid-row: 3;
    min-width: 0;
    margin: 0;
    padding: calc(#{$start-stage-gap-top} + 1px) ($start-stage-pad-x + 1px) 0;
  }

  > .ai-tutor-composer-dock {
    grid-column: 1;
    grid-row: 4;
    min-width: 0;
    margin: 0 ($start-stage-pad-x + 1px)
      ($start-stage-gap-bottom + $start-stage-pad-bottom + 1px);
  }

  // A failed open / new conversation: the note sits under the stage.
  .ai-tutor-chat__error {
    grid-column: 1;
    grid-row: 5;
    justify-self: start;
    margin: -12px 0 10px;
  }
}

.ai-tutor-chat-start {
  padding: 28px 0 22px;
}

.ai-tutor-chat-start__copy {
  display: grid;
  justify-items: center;
  gap: 7px;
  max-width: 780px;
  margin: 0 auto 20px;
  text-align: center;
}

.ai-tutor-chat-start__eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 6px 10px;
  border: 1px solid #e4e9f6;
  border-radius: $borna-radius-pill;
  background: #f7f9fe;
  color: #526188;
  font-size: 11px;
  font-weight: $borna-fw-bold;
  line-height: 1.75;

  i {
    flex: none;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #14b7d7;
    box-shadow: 0 0 0 4px rgba(20, 183, 215, 0.1);
  }
}

.ai-tutor-chat-start__title {
  display: grid;
  gap: 3px;
  margin: 5px 0;
  color: $borna-ink;
  font-size: 20px;
  font-weight: $borna-fw-regular;
  line-height: 1.45;
}

.ai-tutor-chat-start__friend {
  color: #5f6b89;
  font-size: 15px;
  font-weight: $borna-fw-bold;
}

.ai-tutor-chat-start__main {
  font-size: clamp(26px, 2.6vw, 34px);
  font-weight: $borna-fw-black;
  letter-spacing: -0.028em;
}

.ai-tutor-chat-start__support {
  margin: 0;
  color: $borna-muted;
  font-size: 11px;
  font-weight: $borna-fw-medium;
  line-height: 1.9;
  white-space: nowrap;
}

// `.borna-v12-input-guide`: a quiet row as wide as its content, one equal
// column per way to start.
.ai-tutor-chat-start__guide {
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: minmax(0, 1fr);
  width: fit-content;
  max-width: min(100%, 780px);
  margin: 0 auto 12px;
  overflow: hidden;
  border: 1px solid #cfd9f5;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.78);
  box-shadow: 0 8px 20px rgba(49, 75, 214, 0.045);
}

.ai-tutor-chat-start__way {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  padding: 12px 13px;

  & + & {
    border-inline-start: 1px solid #e6ebf5;
  }
}

// Package 38 UX-3: with the Whiteboard the guide is V405's own three-way row
// (`.borna-v12-input-guide`): three equal columns, as wide as their content
// (never wider than the reference's 780px), down to its 620px tier (one
// column below, see the end of this file). The two-way guide above keeps its
// accepted layout unchanged.
.ai-tutor-chat-start__guide--three {
  grid-auto-flow: row;
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.ai-tutor-chat-start__way-icon {
  flex: none;
  display: grid;
  place-items: center;
  width: 34px;
  height: 34px;
  border: 1px solid #e1e7f4;
  border-radius: 11px;
  background: #fff;
  color: $borna-indigo-600;
}

.ai-tutor-chat-start__way-copy {
  display: grid;
  gap: 1px;
  min-width: 0;
  text-align: start;
  overflow-wrap: anywhere;

  strong {
    color: #223664;
    font-size: 10.8px;
    font-weight: $borna-fw-extrabold;
    line-height: 1.7;
  }

  span {
    color: #8792ad;
    font-size: 9.3px;
    line-height: 1.65;
  }
}

// The reference's 900px tier.
@media screen and (max-width: $start-tier-tablet) {
  .ai-tutor-chat--start {
    &::before {
      margin: 24px 0 20px;
    }

    .ai-tutor-chat__timeline {
      padding: 25px 17px 0;
    }

    > .ai-tutor-composer-dock {
      margin: 0 17px (20px + 16px + 1px);
    }
  }

  .ai-tutor-chat-start__friend {
    font-size: 14px;
  }

  .ai-tutor-chat-start__main {
    font-size: 26px;
  }

  .ai-tutor-chat-start__support {
    white-space: normal;
  }
}

// The reference's 620px tier.
@include borna-phone {
  .ai-tutor-chat--start {
    &::before {
      margin: 16px 0 20px;
      border-radius: 22px;
    }

    .ai-tutor-chat__timeline {
      padding: 17px 11px 0;
    }

    > .ai-tutor-composer-dock {
      margin: 0 11px (20px + 10px + 1px);
    }
  }

  .ai-tutor-chat-start {
    padding: 22px 6px 18px;
  }

  .ai-tutor-chat-start__copy {
    gap: 5px;
    margin-bottom: 14px;
  }

  .ai-tutor-chat-start__eyebrow {
    padding: 5px 9px;
    font-size: 9.5px;
  }

  .ai-tutor-chat-start__friend {
    font-size: 12px;
  }

  .ai-tutor-chat-start__main {
    font-size: 21px;
    line-height: 1.55;
  }

  .ai-tutor-chat-start__guide {
    grid-auto-flow: row;
    margin-bottom: 10px;
    border-radius: 15px;
  }

  .ai-tutor-chat-start__way {
    gap: 9px;
    min-height: 48px;
    padding: 9px 10px;

    & + & {
      border-inline-start: 0;
      border-top: 1px solid #e6ebf5;
    }
  }

  .ai-tutor-chat-start__way-icon {
    width: 31px;
    height: 31px;
    border-radius: 10px;

    ::v-deep svg {
      width: 16px;
      height: 16px;
    }
  }

  .ai-tutor-chat-start__way-copy {
    strong {
      font-size: 9.8px;
    }

    span {
      font-size: 8.5px;
    }
  }
}

// Borna's mobile shell, plus the prototype's mobile content padding
// (6px 13px). The shell wrapper is `height: 100%`, so its own bottom-nav
// padding does not extend the scroll area of a long conversation -- the chat
// reserves that space itself, so the last turn always clears the composer.
//
// WP-04V-D: the header height and the floor clearance are now the same tokens
// the header and the bottom nav are drawn from. This media query used to
// switch at 767px while `StudentAppHeader` switched at 640px and subtracted a
// different height, so between those widths the column was sized against a
// header that did not exist (root cause R7). Bottom clearance is the
// composer's own offset (77px) plus its 60px height.
@include borna-mobile {
  .ai-tutor-chat {
    min-height: calc(100vh - #{$borna-header-height-mobile});
    // Student Journey V2: the same side gutters as every other Student page
    // in this band (16px, then 13px from the phone tier down). This was a flat
    // 13px, so the chat sat 3px off the setup that leads into it.
    padding: $borna-page-pad-top-mobile $borna-page-pad-x-mobile
      calc(#{$borna-bottom-nav-clearance + 64px} + env(safe-area-inset-bottom));
  }
}

@include borna-phone {
  .ai-tutor-chat {
    padding-right: $borna-page-pad-x-phone;
    padding-left: $borna-page-pad-x-phone;
  }
}

// CR-03 (doc 28 §17): while the zero-credit warning sits above the FIXED
// composer (its `notice` slot), the column also reserves the warning's
// footprint -- 68px plus the composer's 7px above-area gap -- so the last turn
// and «فهمیدم / نفهمیدم» can always be scrolled clear of it. The docked start
// state keeps its composer in flow and needs nothing.
$credit-notice-reserve: 76px;

.ai-tutor-chat.ai-tutor-chat--credit-notice {
  padding-bottom: 80px + $credit-notice-reserve;
}

@include borna-mobile {
  .ai-tutor-chat.ai-tutor-chat--credit-notice {
    padding-bottom: calc(
      #{$borna-bottom-nav-clearance + 64px + $credit-notice-reserve} + env(safe-area-inset-bottom)
    );
  }
}

// AT-API-020-H1 -- the refused-action notice sits in the same area above the
// fixed composer, so the column reserves its footprint the same way and the
// message it is about can always be scrolled clear of it. The notice is one
// row on a wide column (copy beside its two buttons) and stacks on a narrow
// one, hence the larger mobile reserve. With the zero-credit warning above it
// both footprints are reserved. Sized from the notice's own box model (no
// approved prototype exists for it); to be confirmed on a rendered page.
$action-notice-reserve: 84px;
$action-notice-reserve-mobile: 176px;

.ai-tutor-chat.ai-tutor-chat--action-notice {
  padding-bottom: 80px + $action-notice-reserve;

  &.ai-tutor-chat--credit-notice {
    padding-bottom: 80px + $action-notice-reserve + $credit-notice-reserve;
  }
}

// AT-API-020-H1 P2 remediation (independent audit 2026-10-13, measured on the
// rendered page): on the narrow DESKTOP band the fixed composer is too narrow
// for the notice's one row, so its two buttons stack under the copy
// (120 px tall from 768 px, 92 px around 880–900 px, one row of 68 px from
// 920 px), and the 84 px reserve left the last turn 5–6 px under the notice.
// The band reserves the stacked height; the component is unchanged.
$action-notice-reserve-stacked: 136px;

@media screen and (min-width: 768px) and (max-width: 939.98px) {
  .ai-tutor-chat.ai-tutor-chat--action-notice {
    padding-bottom: 80px + $action-notice-reserve-stacked;

    &.ai-tutor-chat--credit-notice {
      padding-bottom: 80px + $action-notice-reserve-stacked +
        $credit-notice-reserve;
    }
  }
}

@include borna-mobile {
  .ai-tutor-chat.ai-tutor-chat--action-notice {
    padding-bottom: calc(
      #{$borna-bottom-nav-clearance + 64px + $action-notice-reserve-mobile} + env(safe-area-inset-bottom)
    );

    &.ai-tutor-chat--credit-notice {
      padding-bottom: calc(
        #{$borna-bottom-nav-clearance + 64px + $action-notice-reserve-mobile +
          $credit-notice-reserve} + env(safe-area-inset-bottom)
      );
    }
  }
}

// Package 38 UX-3: the three-way guide keeps V405's own tiers -- three
// columns (and the desktop spacing) through its 900px tier, one column only
// from its 620px tier down (`.borna-v12-input-guide` / `.borna-v12-guide-item`).
@media screen and (max-width: $start-tier-tablet) {
  .ai-tutor-chat-start__guide--three {
    grid-template-columns: 1fr 1fr 1fr;
    margin-bottom: 12px;
    border-radius: 18px;

    .ai-tutor-chat-start__way {
      gap: 10px;
      min-height: auto;
      padding: 12px 13px;

      & + .ai-tutor-chat-start__way {
        border-top: 0;
        border-inline-start: 1px solid #e6ebf5;
      }
    }
  }
}

@media screen and (max-width: $start-tier-guide-phone) {
  .ai-tutor-chat-start__guide--three {
    grid-template-columns: 1fr;
    min-width: 0;
    max-width: 100%;
    margin-bottom: 10px;
    border-radius: 15px;

    .ai-tutor-chat-start__way {
      gap: 9px;
      min-height: 48px;
      padding: 9px 10px;

      & + .ai-tutor-chat-start__way {
        border-inline-start: 0;
        border-top: 1px solid #e6ebf5;
      }
    }
  }
}
</style>
